import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, timeout } from 'rxjs/operators';
import { ModalService } from './modal.service';
import { BaseService } from '../shared/services/base.service';

export interface BarcodeScanHandlers {
    rows: any[];
    matchFields?: string[];
    // Fields that only need to match once the row has a value - a blank one absorbs any scan.
    fillableFields?: string[];
    // Falls back to matching this field alone (e.g. itemCode) on a miss - same item, different
    // batch, calls onNewBatchRow instead of onAddNew/onNotFound. Routing only, no qty logic.
    groupField?: string;
    // Default true: confirm before onAddNew. Pass false to add immediately on a miss.
    confirmBeforeAdd?: boolean;
    // Default true. Pass false to disable adding on a miss entirely - onNotFound runs instead.
    allowAdd?: boolean;
    onMatch: (matchIndex: number, qty: number, barcodeItem: any) => void;
    // Same item under a different, already-assigned batch - matchIndex is the group's first row.
    onNewBatchRow?: (matchIndex: number, qty: number, barcodeItem: any) => void;
    onAddNew?: (qty: number, barcodeItem: any) => void;
    onNotFound?: (barcodeItem: any) => void;
}

@Injectable({ providedIn: 'root' })
export class BarcodeGridScanService {
    // Cached (per session) "Batch selection on scan" config (ph_001) - screen ids it's enabled for.
    private batchScanScreens$: Observable<string[]> | undefined;

    constructor(private modalService: ModalService, private baseService: BaseService) { }

    // Fetches ph_001 once per session; fails closed (empty list) on missing/inactive/error/timeout.
    getBatchScanScreens(): Observable<string[]> {
        if (!this.batchScanScreens$) {
            this.batchScanScreens$ = this.baseService.get(
                "GET_CONFIG",
                "module_code:pharmacy,config_code:ph_001",
                "facility_config_mapping",
                "facility-management"
            ).pipe(
                map((res: any) => {
                    const row = res?.resource?.[0];
                    const value = row?.config_value || row?.config;
                    if (res?.errCode !== 0 || !row || row.status !== 'active' || !value) {
                        return [];
                    }
                    return value.split(',').map((v: string) => v.trim()).filter((v: string) => v);
                }),
                timeout(5000),
                catchError(() => of([])),
                shareReplay(1)
            );
        }
        return this.batchScanScreens$;
    }

    // Whether barcode scanning is enabled for a screen id (e.g. 'IP_PHARMACY') per ph_001.
    isBatchScanEnabledForScreen(screenId: string): Observable<boolean> {
        return this.getBatchScanScreens().pipe(map(screens => screens.includes(screenId)));
    }

    computeScanQty(barcodeItem: any): number {
        let qty = 0;
        if (barcodeItem.conversion) {
            qty = barcodeItem.conversion;
        }
        if (barcodeItem.sale_conversion) {
            qty = barcodeItem.is_package_issue ? 1 : barcodeItem.sale_conversion;
        }
        return qty;
    }

    // Sets each key of `values` onto a FormGroup's `.controls` - only keys with a control on this
    // row, values that aren't `undefined`.
    patchControls(controls: any, values: { [key: string]: any }): void {
        if (!controls) {
            return;
        }
        Object.keys(values).forEach(key => {
            const value = values[key];
            const control = controls[key];
            if (control && value !== undefined) {
                control.setValue(value);
                control.updateValueAndValidity();
            }
        });
    }

    // Same idea for a plain object row (e.g. localOrderSet items, not FormGroups).
    patchObject(target: any, values: { [key: string]: any }): void {
        if (!target) {
            return;
        }
        Object.keys(values).forEach(key => {
            if (values[key] !== undefined) {
                target[key] = values[key];
            }
        });
    }

    // Sums issueQty across every plain-object row (e.g. localOrderSet) sharing itemCode - reqQty is
    // per item, not per row/batch, when the resource even has a reqQty field.
    sumIssueQtyForItemCodeInRows(rows: any[], itemCode: any, excludeIndex?: number): number {
        return (rows || []).reduce((sum, row, idx) => {
            if (idx === excludeIndex || row.itemCode !== itemCode) {
                return sum;
            }
            return sum + (row.issueQty || 0);
        }, 0);
    }

    // When enabled, resets the qty control to 0 for rows without a batch yet (scan must fill it
    // instead). qtyField names that control - screens outside pharmacy use a different one
    // (e.g. 'issuedquantity' on indent issue).
    resetIssueQtyForUnbatchedRows(rows: any[], enabled: boolean, qtyField: string = 'issueQty'): void {
        if (!enabled || !rows) {
            return;
        }
        for (const rowGroup of rows) {
            const row = rowGroup?.controls;
            if (!row) {
                continue;
            }
            const batchFilled = !!(row.batchNo?.value && row.batchId?.value);
            if (!batchFilled && row[qtyField]) {
                row[qtyField].setValue(0);
                row[qtyField].updateValueAndValidity();
            }
        }
    }

    // Matches a row on matchFields/fillableFields -> onMatch; else groupField -> onNewBatchRow;
    // else onNotFound/onAddNew (with confirm). Routing only - qty/validation is the caller's job.
    handleScannedItem(barcodeItem: any, handlers: BarcodeScanHandlers): void {
        if (!barcodeItem) {
            return;
        }
        const matchFields = handlers.matchFields && handlers.matchFields.length ? handlers.matchFields : ['itemCode'];
        const fillableFields = handlers.fillableFields || [];
        const rows = handlers.rows || [];
        const qty = this.computeScanQty(barcodeItem);

        const fieldMatches = (row: any, field: string): boolean => {
            const rowVal = row[field];
            if (fillableFields.includes(field) && (rowVal === undefined || rowVal === null || rowVal === '')) {
                return true;
            }
            return (rowVal ?? '') == (barcodeItem[field] ?? '');
        };
        const matchIndex = rows.findIndex(row => row && [...matchFields, ...fillableFields].every(field => fieldMatches(row, field)));

        if (matchIndex >= 0) {
            handlers.onMatch(matchIndex, qty, barcodeItem);
            return;
        }

        if (handlers.groupField) {
            const groupField = handlers.groupField;
            const groupIndex = rows.findIndex(row => row && (row[groupField] ?? '') == (barcodeItem[groupField] ?? ''));
            if (groupIndex >= 0) {
                handlers.onNewBatchRow?.(groupIndex, qty, barcodeItem);
                return;
            }
        }

        if (handlers.allowAdd === false) {
            handlers.onNotFound?.(barcodeItem);
            return;
        }
        if (handlers.confirmBeforeAdd === false) {
            handlers.onAddNew?.(qty, barcodeItem);
            return;
        }
        const dialogRef = this.modalService.confirm(
            "Confirm",
            `"${barcodeItem.itemName || 'Item'}" not found in the list. Do you want to add this item in the grid?`,
            'sm'
        );
        dialogRef.subscribe((res: boolean) => {
            if (res) {
                handlers.onAddNew?.(qty, barcodeItem);
            }
        });
    }
}
